class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('submit', this.onUpsellSubmit.bind(this));
    this.addEventListener('click', this.onRemoveClick.bind(this));
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onRemoveClick(event) {
    if (event.defaultPrevented) return;

    const removeButton = event.target.closest('cart-remove-button');
    if (!removeButton || !this.contains(removeButton)) return;

    const cartItems = removeButton.closest('cart-drawer-items');
    if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;

    event.preventDefault();
    cartItems.updateQuantity(removeButton.dataset.index, 0, event);
  }

  onUpsellSubmit(event) {
    const form = event.target.closest('.scrubly-upsell__form');
    if (!form) return;

    event.preventDefault();

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton?.getAttribute('aria-disabled') === 'true') return;

    const formData = new FormData(form);
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    formData.append(
      'sections',
      this.getSectionsToRender()
        .map((section) => section.id)
        .join(',')
    );
    formData.append('sections_url', window.location.pathname);

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.disabled = true;
    }

    config.body = formData;
    this.setActiveElement(submitButton || document.activeElement);

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Unable to add upsell');
        }

        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = '';
        this.renderContents(response);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer',
          cartData: response,
          variantId: formData.get('id'),
        });
      })
      .catch((error) => {
        console.error(error);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        if (submitButton) {
          submitButton.removeAttribute('aria-disabled');
          submitButton.disabled = false;
        }
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHtml) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    const itemCount = this.resolveItemCount(parsedState);
    this.syncDrawerState(itemCount);

    setTimeout(() => {
      this.syncDrawerState(itemCount);
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  resolveItemCount(parsedState) {
    if (typeof parsedState?.item_count === 'number') return parsedState.item_count;
    if (Array.isArray(parsedState?.items)) return parsedState.items.length;

    return this.querySelectorAll('#CartDrawer-CartItems .cart-item').length;
  }

  syncDrawerState(itemCount) {
    const resolvedItemCount = typeof itemCount === 'number' ? itemCount : this.resolveItemCount();
    const isEmpty = resolvedItemCount === 0;

    this.classList.toggle('is-empty', isEmpty);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
    this.querySelector('.scrubly-shipping-bar')?.classList.toggle('is-hidden', isEmpty);
    this.querySelector('#CartDrawer-Checkout')?.toggleAttribute('disabled', isEmpty);

    if (!isEmpty) {
      this.querySelector('.drawer__inner-empty')?.remove();
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');
      this.querySelector('.cart__contents')?.classList.remove('is-empty');
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : parsed.body.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
